const DB_NAME = 'model-store';
const STORE_NAME = 'models';
const DB_VERSION = 1;

/**
 * 基于 IndexedDB 的模型持久化存储
 * 将导入的模型文件以 ArrayBuffer 形式保存在浏览器本地
 */
export class ModelStorage {
  /** @type {IDBDatabase | null} */
  #db = null;

  /** 打开/初始化数据库 */
  #openDB() {
    return new Promise((resolve, reject) => {
      if (this.#db) return resolve(this.#db);
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        }
      };
      req.onsuccess = () => {
        this.#db = req.result;
        resolve(this.#db);
      };
      req.onerror = () => reject(req.error);
    });
  }

  /**
   * 保存模型到 IndexedDB
   * @param {string} id
   * @param {object} data
   */
  async save(id, data) {
    const db = await this.#openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).put({ id, ...data, savedAt: Date.now() });
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  /**
   * 根据 ID 获取模型数据
   * @param {string} id
   * @returns {Promise<object | null>}
   */
  async get(id) {
    const db = await this.#openDB();
    return new Promise((resolve, reject) => {
      const req = db.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME).get(id);
      req.onsuccess = () => resolve(req.result ?? null);
      req.onerror = () => reject(req.error);
    });
  }

  /**
   * 列出所有已保存的模型元数据
   * @returns {Promise<Array<{id: string, name: string, fileName: string, animationCount: number, thumbnail?: string}>>}
   */
  async list() {
    const db = await this.#openDB();
    return new Promise((resolve, reject) => {
      const req = db.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME).getAll();
      req.onsuccess = () => {
        const items = (req.result || []).map(({ id, name, fileName, animationCount, thumbnail, savedAt }) => ({
          id, name, fileName, animationCount, thumbnail, savedAt,
        }));
        resolve(items.sort((a, b) => (b.savedAt || 0) - (a.savedAt || 0)));
      };
      req.onerror = () => reject(req.error);
    });
  }

  /**
   * 删除模型
   * @param {string} id
   */
  async remove(id) {
    const db = await this.#openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).delete(id);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  /** 清空所有模型 */
  async clear() {
    const db = await this.#openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).clear();
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }
}
